import React, { useMemo, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Input, Modal, Radio, Select, Table, Tag, Typography, Empty, message } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { DesktopOutlined, AppstoreOutlined, BulbOutlined, SoundOutlined, PlusOutlined, UploadOutlined, DeleteOutlined, SettingOutlined, TableOutlined, UnorderedListOutlined, EyeOutlined } from '@ant-design/icons';

import { useDb } from '../../db/useDataService';
import * as ds from '../../db/dataService';
import CreateCampaignModal from '../campaigns/CreateCampaignModal';
import AdImageEditor, { CropModal } from '../AdImageEditor';
import { usePersistedState } from '../../hooks/usePersistedState';
import { DEFAULT_AD_IMAGE_SETTINGS } from '../../types';
import {
  Campaign,
  AdGroup,
  CampaignAd,
  CampaignStatus,
  CAMPAIGN_STATUS_CONFIG,
  PLACEMENT_CONFIG,
  GROUPING_LEVEL_CONFIG,
  ANGLE_TYPE_CONFIG,
  AdPlatform,
  AdStatus,
  AdCabinet,
  SmOffer,
  LifeSphere,
} from '../../types';

const AD_STATUS_CONFIG: Record<AdStatus, { label: string; color: string }> = {
  draft: { label: 'Черновик', color: 'default' },
  active: { label: 'Активно', color: 'green' },
  paused: { label: 'Пауза', color: 'orange' },
};

const EditCell: React.FC<{ value: string; max: number; onChange: (v: string) => void; placeholder?: string }> = ({ value, max, onChange, placeholder }) => {
  const len = (value || '').length;
  const over = len > max;
  return (
    <div className="ads-edit-cell">
      <Input.TextArea
        autoSize={{ minRows: 1 }}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={{ fontSize: 12 }}
      />
      <span className={`ads-edit-cell__count${over ? ' ads-edit-cell__count--over' : ''}`}>{len}/{max}</span>
    </div>
  );
};

const { Text } = Typography;

type Level = 'platforms' | 'cabinets' | 'campaigns' | 'groups' | 'ads';

const PLATFORM_LABEL: Record<AdPlatform, string> = {
  yandex_direct: 'Яндекс Директ',
  vk: 'VK Ads',
};
const PLATFORM_LIST: AdPlatform[] = ['yandex_direct', 'vk'];

const AD_STATUS_LABEL: Record<string, { label: string; color: string }> = {
  draft: { label: 'Черновик', color: 'default' },
  active: { label: 'Активно', color: 'green' },
  paused: { label: 'Пауза', color: 'orange' },
};

const formatMoney = (n: number) => new Intl.NumberFormat('ru-RU').format(n) + ' ₽';
const formatDate = (iso: string) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('ru-RU');
};

interface AdsTabProps {
  productId: string;
  cabinets: AdCabinet[];
  offers: SmOffer[];
  spheres: LifeSphere[];
}

const AdsTab: React.FC<AdsTabProps> = ({ productId, cabinets, offers, spheres }) => {
  const db = useDb();
  const allCampaigns = useMemo(() => db.campaigns.filter((c) => c.productId === productId), [db.campaigns, productId]);
  const allAdGroups = db.adGroups;
  const allAds = db.campaignAds;

  const [searchParams, setSearchParams] = useSearchParams();
  const selectedPlatform = (searchParams.get('adPlatform') as AdPlatform | null) || null;
  const selectedCabinetId = searchParams.get('adCabinet');
  const selectedCampaignId = searchParams.get('adCampaign');
  const selectedGroupId = searchParams.get('adGroup');
  const currentLevel = (searchParams.get('adLevel') as Level | null) || 'platforms';

  const updateAdParams = useCallback((updates: Record<string, string | null>) => {
    const params = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([k, v]) => {
      if (v === null || v === '') params.delete(k);
      else params.set(k, v);
    });
    // adLevel всегда в конце URL — удаляем и заново ставим
    const lvl = params.get('adLevel');
    if (lvl !== null) {
      params.delete('adLevel');
      params.set('adLevel', lvl);
    }
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [editingImageAdId, setEditingImageAdId] = useState<string | null>(null);
  const [cropState, setCropState] = useState<{ adId: string; dataUrl: string } | null>(null);
  const [adsViewMode, setAdsViewMode] = usePersistedState<'columns' | 'rows'>('adsViewMode', 'columns');
  const [previewAdId, setPreviewAdId] = useState<string | null>(null);

  const renderAdPreview = (ad: CampaignAd) => (
    <div className="ya-preview">
      <div className="ya-preview__img-section">
        {ad.image ? (
          <img src={ad.image} alt="" className="ya-preview__img" />
        ) : (
          <div className="ya-preview__img-placeholder" />
        )}
        <div className="ya-preview__badge">РЕКЛАМА · 16+</div>
        <div className="ya-preview__dots">•••</div>
      </div>
      <div className="ya-preview__content">
        <div className="ya-preview__domain">
          <div className="ya-preview__favicon" />
          <span>{ad.displayUrl || ad.url}</span>
        </div>
        <div className="ya-preview__title">{ad.title}{ad.title2 ? `, ${ad.title2}` : ''}</div>
        <div className="ya-preview__text">{ad.text}</div>
        {ad.sitelinks && ad.sitelinks.some((sl) => sl && sl.title) && (
          <div className="ya-preview__sitelinks">
            {ad.sitelinks.filter((sl) => sl && sl.title).map((sl, i) => (
              <div key={i} className="ya-preview__sitelink">
                <span>{sl.title}</span>
                <span className="ya-preview__sitelink-arrow">&gt;</span>
              </div>
            ))}
          </div>
        )}
        <div className="ya-preview__btn">{ad.actionButton || 'Перейти'}</div>
      </div>
    </div>
  );

  const offerName = (id: string) => offers.find((o) => o.id === id)?.name || id;

  const platformCabinets = useMemo(
    () => (selectedPlatform ? cabinets.filter((c) => c.platform === selectedPlatform) : []),
    [cabinets, selectedPlatform],
  );
  const selectedCabinet = useMemo(
    () => platformCabinets.find((c) => c.id === selectedCabinetId) || null,
    [platformCabinets, selectedCabinetId],
  );
  const cabinetCampaigns = useMemo(
    () => (selectedCabinetId ? allCampaigns.filter((c) => c.cabinetId === selectedCabinetId) : []),
    [allCampaigns, selectedCabinetId],
  );
  const selectedCampaign = useMemo(
    () => cabinetCampaigns.find((c) => c.id === selectedCampaignId) || null,
    [cabinetCampaigns, selectedCampaignId],
  );
  const campaignGroups = useMemo(
    () => (selectedCampaignId ? allAdGroups.filter((g) => g.campaignId === selectedCampaignId) : []),
    [allAdGroups, selectedCampaignId],
  );
  const selectedGroup = useMemo(
    () => campaignGroups.find((g) => g.id === selectedGroupId) || null,
    [campaignGroups, selectedGroupId],
  );
  const groupAds = useMemo(
    () => (selectedGroupId ? allAds.filter((a) => a.adGroupId === selectedGroupId) : []),
    [allAds, selectedGroupId],
  );

  const handleSelectPlatform = (p: AdPlatform) => {
    updateAdParams({ adPlatform: p, adCabinet: null, adCampaign: null, adGroup: null, adLevel: 'cabinets' });
  };
  const handleSelectCabinet = (id: string) => {
    updateAdParams({ adCabinet: id, adCampaign: null, adGroup: null, adLevel: 'campaigns' });
  };
  const handleSelectCampaign = (id: string) => {
    updateAdParams({ adCampaign: id, adGroup: null, adLevel: 'groups' });
  };
  const handleSelectGroup = (id: string) => {
    updateAdParams({ adGroup: id, adLevel: 'ads' });
  };

  const handleClickTab = (k: Level) => {
    if (k === 'platforms') updateAdParams({ adLevel: null });
    else if (k === 'cabinets' && selectedPlatform) updateAdParams({ adLevel: 'cabinets' });
    else if (k === 'campaigns' && selectedCabinetId) updateAdParams({ adLevel: 'campaigns' });
    else if (k === 'groups' && selectedCampaignId) updateAdParams({ adLevel: 'groups' });
    else if (k === 'ads' && selectedGroupId) updateAdParams({ adLevel: 'ads' });
  };

  const navTabs: { key: Level; label: string; icon: React.ReactNode; selectedName: string | null; enabled: boolean }[] = [
    { key: 'platforms', label: 'Рекламные площадки', icon: <DesktopOutlined className="sm-nav__tab-icon" />, selectedName: selectedPlatform ? PLATFORM_LABEL[selectedPlatform] : null, enabled: true },
    { key: 'cabinets', label: 'Рекламные кабинеты', icon: <DesktopOutlined className="sm-nav__tab-icon" />, selectedName: selectedCabinet?.name || null, enabled: !!selectedPlatform },
    { key: 'campaigns', label: 'Кампании', icon: <AppstoreOutlined className="sm-nav__tab-icon" />, selectedName: selectedCampaign?.name || null, enabled: !!selectedCabinetId },
    { key: 'groups', label: 'Группы объявлений', icon: <BulbOutlined className="sm-nav__tab-icon" />, selectedName: selectedGroup?.name || null, enabled: !!selectedCampaignId },
    { key: 'ads', label: 'Объявления', icon: <SoundOutlined className="sm-nav__tab-icon" />, selectedName: null, enabled: !!selectedGroupId },
  ];

  const renderPlatformsTable = () => {
    const rows = PLATFORM_LIST.map((p) => {
      const camps = allCampaigns.filter((c) => c.platform === p);
      const campIds = new Set(camps.map((c) => c.id));
      const groups = allAdGroups.filter((g) => campIds.has(g.campaignId));
      const ads = allAds.filter((a) => campIds.has(a.campaignId));
      const active = camps.filter((c) => c.status === 'active').length;
      return { key: p, platform: p, label: PLATFORM_LABEL[p], campaignsCount: camps.length, activeCount: active, groupsCount: groups.length, adsCount: ads.length };
    });
    const cols: ColumnsType<typeof rows[number]> = [
      { title: 'Площадка', dataIndex: 'label', key: 'label', render: (v: string) => <Text strong>{v}</Text> },
      { title: 'Кампаний', dataIndex: 'campaignsCount', key: 'campaignsCount', width: 120, align: 'right' },
      { title: 'Активных', dataIndex: 'activeCount', key: 'activeCount', width: 120, align: 'right' },
      { title: 'Групп', dataIndex: 'groupsCount', key: 'groupsCount', width: 100, align: 'right' },
      { title: 'Объявлений', dataIndex: 'adsCount', key: 'adsCount', width: 130, align: 'right' },
    ];
    return (
      <Table
        rowKey="key"
        dataSource={rows}
        columns={cols}
        pagination={false}
        size="small"
        onRow={(r) => ({ onClick: () => handleSelectPlatform(r.platform), style: { cursor: 'pointer' } })}
      />
    );
  };

  const renderCabinetsTable = () => {
    if (platformCabinets.length === 0) return <Empty description="Нет рекламных кабинетов на этой площадке" style={{ padding: '32px 0' }} />;
    const rows = platformCabinets.map((cab) => {
      const camps = allCampaigns.filter((c) => c.cabinetId === cab.id);
      const campIds = new Set(camps.map((c) => c.id));
      const groups = allAdGroups.filter((g) => campIds.has(g.campaignId));
      const ads = allAds.filter((a) => campIds.has(a.campaignId));
      return { ...cab, campaignsCount: camps.length, activeCount: camps.filter((c) => c.status === 'active').length, groupsCount: groups.length, adsCount: ads.length };
    });
    const cols: ColumnsType<typeof rows[number]> = [
      { title: 'Название', dataIndex: 'name', key: 'name', render: (v: string) => <Text strong>{v}</Text> },
      { title: 'External ID', dataIndex: 'externalId', key: 'externalId', width: 160, render: (v: string) => <Text type="secondary">{v || '—'}</Text> },
      { title: 'Кампаний', dataIndex: 'campaignsCount', key: 'campaignsCount', width: 110, align: 'right' },
      { title: 'Активных', dataIndex: 'activeCount', key: 'activeCount', width: 110, align: 'right' },
      { title: 'Групп', dataIndex: 'groupsCount', key: 'groupsCount', width: 90, align: 'right' },
      { title: 'Объявлений', dataIndex: 'adsCount', key: 'adsCount', width: 120, align: 'right' },
    ];
    return (
      <Table
        rowKey="id"
        dataSource={rows}
        columns={cols}
        pagination={false}
        size="small"
        onRow={(r) => ({ onClick: () => handleSelectCabinet(r.id), style: { cursor: 'pointer' } })}
      />
    );
  };

  const renderCampaignsTable = () => {
    if (cabinetCampaigns.length === 0) return <Empty description="Нет кампаний в этом кабинете" style={{ padding: '32px 0' }} />;
    const cols: ColumnsType<Campaign> = [
      { title: 'Название', dataIndex: 'name', key: 'name', render: (v: string) => <Text strong>{v}</Text> },
      { title: 'Статус', dataIndex: 'status', key: 'status', width: 110, render: (s: CampaignStatus) => <Tag color={CAMPAIGN_STATUS_CONFIG[s].color}>{CAMPAIGN_STATUS_CONFIG[s].label}</Tag> },
      { title: 'Размещение', dataIndex: 'placement', key: 'placement', width: 130, render: (p) => PLACEMENT_CONFIG[p as keyof typeof PLACEMENT_CONFIG] || p },
      { title: 'Оффер', dataIndex: 'offerId', key: 'offerId', width: 180, render: (id) => offerName(id) },
      { title: 'Бюджет / нед.', dataIndex: 'weeklyBudget', key: 'weeklyBudget', width: 130, align: 'right', render: (n: number) => formatMoney(n) },
      { title: 'Групп', key: 'groups', width: 80, align: 'right', render: (_: unknown, r) => allAdGroups.filter((g) => g.campaignId === r.id).length },
      { title: 'Объявл.', key: 'ads', width: 90, align: 'right', render: (_: unknown, r) => allAds.filter((a) => a.campaignId === r.id).length },
      { title: 'Создана', dataIndex: 'createdAt', key: 'createdAt', width: 110, render: (d: string) => formatDate(d) },
    ];
    return (
      <Table
        rowKey="id"
        dataSource={cabinetCampaigns}
        columns={cols}
        pagination={false}
        size="small"
        scroll={{ x: 1200 }}
        onRow={(r) => ({ onClick: () => handleSelectCampaign(r.id), style: { cursor: 'pointer' } })}
      />
    );
  };

  const renderGroupsTable = () => {
    if (campaignGroups.length === 0) return <Empty description="Нет групп объявлений в этой кампании" style={{ padding: '32px 0' }} />;
    const cols: ColumnsType<AdGroup> = [
      { title: 'Название', dataIndex: 'name', key: 'name', render: (v: string) => <Text strong>{v}</Text> },
      { title: 'Уровень', dataIndex: 'sourceLevel', key: 'sourceLevel', width: 140, render: (l) => GROUPING_LEVEL_CONFIG[l as keyof typeof GROUPING_LEVEL_CONFIG] || l },
      { title: 'Ключей', key: 'keywords', width: 90, align: 'right', render: (_: unknown, r) => r.keywords.length + r.suKeywords.length },
      { title: 'Объявл.', key: 'ads', width: 90, align: 'right', render: (_: unknown, r) => allAds.filter((a) => a.adGroupId === r.id).length },
      { title: 'Активна', dataIndex: 'enabled', key: 'enabled', width: 100, render: (v: boolean) => <Tag color={v ? 'green' : 'default'}>{v ? 'Да' : 'Нет'}</Tag> },
    ];
    return (
      <Table
        rowKey="id"
        dataSource={campaignGroups}
        columns={cols}
        pagination={false}
        size="small"
        onRow={(r) => ({ onClick: () => handleSelectGroup(r.id), style: { cursor: 'pointer' } })}
      />
    );
  };

  const updateAd = (id: string, field: keyof CampaignAd, value: any) => {
    ds.setCampaignAds(allAds.map((a) => a.id === id ? { ...a, [field]: value } : a));
  };
  const updateAdSitelink = (id: string, idx: number, field: 'title' | 'description' | 'url', value: string) => {
    ds.setCampaignAds(allAds.map((a) => {
      if (a.id !== id) return a;
      const sitelinks = [...a.sitelinks] as [any, any, any, any];
      sitelinks[idx] = { ...sitelinks[idx], [field]: value };
      return { ...a, sitelinks };
    }));
  };
  const handleAdImageUpload = (adId: string, file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      const img = new Image();
      img.onload = () => {
        const ratio = img.width / img.height;
        if (ratio > 4 / 3 + 0.05) {
          setCropState({ adId, dataUrl });
        } else {
          updateAd(adId, 'image', dataUrl);
          message.success('Изображение загружено');
        }
      };
      img.src = dataUrl;
    };
    reader.readAsDataURL(file);
  };
  const handleCropComplete = (croppedDataUrl: string) => {
    if (!cropState) return;
    updateAd(cropState.adId, 'image', croppedDataUrl);
    setCropState(null);
    message.success('Изображение обрезано и загружено');
  };
  const handleAdImageRemove = (id: string) => {
    ds.setCampaignAds(allAds.map((a) => a.id === id ? { ...a, image: undefined, imageSettings: undefined } : a));
  };

  const renderAdsTable = () => {
    if (groupAds.length === 0) return <Empty description="Нет объявлений в этой группе" style={{ padding: '32px 0' }} />;
    const isYa = selectedPlatform === 'yandex_direct';

    type RowDef = { key: string; label: string; render: (ad: CampaignAd) => React.ReactNode };
    const rows: RowDef[] = [];

    rows.push({ key: 'name', label: 'Название объявления (255)', render: (ad) => <EditCell value={ad.name || ''} max={255} onChange={(v) => updateAd(ad.id, 'name', v)} placeholder="Внутреннее название (например, с кодом s12.c34...)" /> });
    rows.push({ key: 'status', label: 'Статус', render: (ad) => (
      <Select size="small" value={ad.status} onChange={(v) => updateAd(ad.id, 'status', v)} style={{ width: '100%', fontSize: 12 }}>
        {(Object.keys(AD_STATUS_CONFIG) as AdStatus[]).map((s) => (
          <Select.Option key={s} value={s}><Tag color={AD_STATUS_CONFIG[s].color} style={{ fontSize: 11 }}>{AD_STATUS_CONFIG[s].label}</Tag></Select.Option>
        ))}
      </Select>
    ) });
    rows.push({ key: 'angle', label: 'Стиль', render: (ad) => <Text style={{ fontSize: 12 }}>{ANGLE_TYPE_CONFIG[ad.angleType]?.label || ad.angleType}</Text> });
    rows.push({ key: 'variant', label: 'Вариант', render: (ad) => <Text style={{ fontSize: 12 }}>{ad.variantIndex}</Text> });
    rows.push({ key: 'title', label: 'Заголовок 1 (56)', render: (ad) => <EditCell value={ad.title} max={56} onChange={(v) => updateAd(ad.id, 'title', v)} placeholder="Заголовок объявления" /> });
    if (isYa) rows.push({ key: 'title2', label: 'Заголовок 2 (30)', render: (ad) => <EditCell value={ad.title2} max={30} onChange={(v) => updateAd(ad.id, 'title2', v)} placeholder="Дополнительный заголовок" /> });
    rows.push({ key: 'text', label: 'Текст (81)', render: (ad) => <EditCell value={ad.text} max={81} onChange={(v) => updateAd(ad.id, 'text', v)} placeholder="Текст объявления" /> });
    rows.push({ key: 'url', label: 'Ссылка (1024)', render: (ad) => <EditCell value={ad.url} max={1024} onChange={(v) => updateAd(ad.id, 'url', v)} placeholder="https://..." /> });
    if (isYa) rows.push({ key: 'displayUrl', label: 'Отобр. ссылка (20)', render: (ad) => <EditCell value={ad.displayUrl} max={20} onChange={(v) => updateAd(ad.id, 'displayUrl', v)} placeholder="путь/после/домена" /> });

    if (isYa) {
      for (let i = 0; i < 4; i++) {
        const n = i + 1;
        rows.push({ key: `sl${n}_title`, label: `БС ${n} — загол. (30)`, render: (ad) => <EditCell value={ad.sitelinks[i]?.title || ''} max={30} onChange={(v) => updateAdSitelink(ad.id, i, 'title', v)} placeholder={`Быстрая ссылка ${n}`} /> });
        rows.push({ key: `sl${n}_desc`, label: `БС ${n} — описание (60)`, render: (ad) => <EditCell value={ad.sitelinks[i]?.description || ''} max={60} onChange={(v) => updateAdSitelink(ad.id, i, 'description', v)} placeholder="Описание" /> });
        rows.push({ key: `sl${n}_url`, label: `БС ${n} — ссылка`, render: (ad) => <EditCell value={ad.sitelinks[i]?.url || ''} max={1024} onChange={(v) => updateAdSitelink(ad.id, i, 'url', v)} placeholder="https://..." /> });
      }
    }

    rows.push({ key: 'callouts', label: 'Уточнения (25×4)', render: (ad) => <EditCell value={(ad.callouts || []).join(', ')} max={132} onChange={(v) => updateAd(ad.id, 'callouts', v.split(',').map((s) => s.trim()).filter(Boolean))} placeholder="Через запятую" /> });
    rows.push({ key: 'actionButton', label: 'Кнопка действия', render: (ad) => (
      <Select size="small" value={ad.actionButton} onChange={(v) => updateAd(ad.id, 'actionButton', v)} style={{ width: '100%', fontSize: 12 }}>
        {['Перейти', 'Скачать', 'Купить', 'Записаться', 'Подробнее', 'Узнать больше', 'Зарегистрироваться', 'Заказать', 'Попробовать'].map((b) => (
          <Select.Option key={b} value={b}>{b}</Select.Option>
        ))}
      </Select>
    ) });
    rows.push({ key: 'imageText', label: 'Текст на картинку', render: (ad) => <EditCell value={ad.imageText} max={100} onChange={(v) => updateAd(ad.id, 'imageText', v)} placeholder="Текст поверх изображения" /> });
    rows.push({ key: 'imageButtonText', label: 'Кнопка на картинку', render: (ad) => <EditCell value={ad.imageButtonText} max={40} onChange={(v) => updateAd(ad.id, 'imageButtonText', v)} placeholder="Текст кнопки" /> });
    rows.push({ key: 'imageStyle', label: 'Стиль картинки', render: (ad) => (
      <Button size="small" icon={<SettingOutlined />} onClick={() => setEditingImageAdId(ad.id)} disabled={!ad.image} style={{ fontSize: 12 }}>Настройки</Button>
    ) });
    rows.push({ key: 'image', label: 'Изображение', render: (ad) => ad.image ? (
      <div className="ads-table__img-wrap">
        <img src={ad.image} alt="" className="ads-table__img" />
        <div className="ads-table__img-actions">
          <label className="ads-table__img-btn">
            <UploadOutlined />
            <input type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => { const f = e.target.files?.[0]; if (f) handleAdImageUpload(ad.id, f); }} />
          </label>
          <button className="ads-table__img-btn" onClick={() => handleAdImageRemove(ad.id)}><DeleteOutlined /></button>
        </div>
      </div>
    ) : (
      <label className="ads-table__img-upload">
        <UploadOutlined style={{ fontSize: 16, color: '#bfbfbf' }} />
        <span style={{ fontSize: 11, color: '#bfbfbf' }}>Загрузить</span>
        <input type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => { const f = e.target.files?.[0]; if (f) handleAdImageUpload(ad.id, f); }} />
      </label>
    ) });

    rows.push({ key: 'preview', label: 'Превью', render: (ad) => (
      adsViewMode === 'rows'
        ? <Button size="small" icon={<EyeOutlined />} onClick={() => setPreviewAdId(ad.id)}>Показать превью</Button>
        : renderAdPreview(ad)
    ) });

    if (adsViewMode === 'rows') {
      const cols: ColumnsType<CampaignAd> = rows.map((row) => ({
        title: row.label,
        key: row.key,
        width: 280,
        render: (_: unknown, ad: CampaignAd) => row.render(ad),
      }));
      return <Table rowKey="id" dataSource={groupAds} columns={cols} pagination={false} size="small" scroll={{ x: 'max-content' }} />;
    }

    return (
      <div className="ads-table-wrap">
        <table className="ads-table">
          <tbody>
            {rows.map((row) => (
              <tr key={row.key}>
                <th className="ads-table__label">{row.label}</th>
                {groupAds.map((ad) => (
                  <td key={ad.id} className="ads-table__cell">{row.render(ad)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="semantic-map">
      <div className="sm-nav">
        {navTabs.map((tab) => {
          const isActive = currentLevel === tab.key;
          const isClickable = tab.enabled;
          const isDisabled = !tab.enabled;
          let cn = 'sm-nav__tab';
          if (isActive) cn += ' sm-nav__tab--active';
          if (isDisabled) cn += ' sm-nav__tab--disabled';
          return (
            <div key={tab.key} className={cn} onClick={isClickable ? () => handleClickTab(tab.key) : undefined}>
              <div className="sm-nav__tab-row">
                {tab.icon}
                <Text className="sm-nav__tab-label">{tab.label}</Text>
              </div>
              {tab.selectedName && <div className="sm-nav__tab-chip">{tab.selectedName}</div>}
            </div>
          );
        })}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
        <div>
          {currentLevel === 'ads' && (
            <Radio.Group value={adsViewMode} onChange={(e) => setAdsViewMode(e.target.value)} size="middle">
              <Radio.Button value="columns"><TableOutlined /> Колонки</Radio.Button>
              <Radio.Button value="rows"><UnorderedListOutlined /> Строки</Radio.Button>
            </Radio.Group>
          )}
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setCreateModalVisible(true)}
          style={{ backgroundColor: '#ff4d4f', borderColor: '#ff4d4f', borderRadius: 8 }}
        >
          Создать кампанию
        </Button>
      </div>

      <div style={{ marginTop: 12 }}>
        {currentLevel === 'platforms' && renderPlatformsTable()}
        {currentLevel === 'cabinets' && renderCabinetsTable()}
        {currentLevel === 'campaigns' && renderCampaignsTable()}
        {currentLevel === 'groups' && renderGroupsTable()}
        {currentLevel === 'ads' && renderAdsTable()}
      </div>

      <CreateCampaignModal
        visible={createModalVisible}
        onClose={() => setCreateModalVisible(false)}
        onCreate={(newCampaigns, groups, ads) => {
          ds.setCampaigns(productId, [...newCampaigns, ...allCampaigns]);
          ds.setAdGroups([...allAdGroups, ...groups]);
          ds.setCampaignAds([...allAds, ...ads]);
          setCreateModalVisible(false);
          message.success(
            newCampaigns.length > 1
              ? `Создано ${newCampaigns.length} кампаний, ${groups.length} групп, ${ads.length} объявлений`
              : `Создана кампания, ${groups.length} групп, ${ads.length} объявлений`,
          );
          const first = newCampaigns[0];
          if (first) {
            updateAdParams({
              adPlatform: first.platform,
              adCabinet: first.cabinetId,
              adCampaign: first.id,
              adGroup: null,
              adLevel: 'groups',
            });
          }
        }}
        productId={productId}
        spheres={spheres}
        offers={offers}
        cabinets={cabinets}
      />

      {editingImageAdId && (() => {
        const editingAd = allAds.find((a) => a.id === editingImageAdId);
        if (!editingAd || !editingAd.image) return null;
        return (
          <AdImageEditor
            visible={true}
            imageSrc={editingAd.image}
            settings={editingAd.imageSettings || { ...DEFAULT_AD_IMAGE_SETTINGS, overlayText: editingAd.title }}
            adTitle={editingAd.title}
            onClose={() => setEditingImageAdId(null)}
            onSave={(imgSettings) => {
              updateAd(editingImageAdId, 'imageSettings', imgSettings);
              message.success('Настройки изображения сохранены');
            }}
            onImageChange={(file) => handleAdImageUpload(editingImageAdId, file)}
          />
        );
      })()}

      <CropModal
        visible={!!cropState}
        imageSrc={cropState?.dataUrl || ''}
        onCropComplete={handleCropComplete}
        onCancel={() => setCropState(null)}
      />

      <Modal
        title="Превью объявления"
        visible={!!previewAdId}
        onCancel={() => setPreviewAdId(null)}
        footer={<Button onClick={() => setPreviewAdId(null)}>Закрыть</Button>}
        width={520}
        destroyOnClose
      >
        {previewAdId && (() => {
          const ad = allAds.find((a) => a.id === previewAdId);
          return ad ? renderAdPreview(ad) : <Empty description="Объявление не найдено" />;
        })()}
      </Modal>
    </div>
  );
};

export default AdsTab;
